import * as z from "zod";

const researchSourceSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]+$/),
  title: z.string(),
  url: z.string().refine((value) => {
    try {
      const url = new URL(value);
      return (
        (url.protocol === "https:" || url.protocol === "http:") &&
        !url.username &&
        !url.password
      );
    } catch {
      return false;
    }
  }),
  excerpts: z.array(z.string()).min(1),
  preview: z.string().optional(),
  publishDate: z.string().optional(),
});

export type ResearchSource = z.infer<typeof researchSourceSchema>;

export function readResearchSources(output: unknown): ResearchSource[] {
  const resultSchema = z.object({
    sourceId: z.string(),
    url: z.string(),
    title: z.string().optional(),
    excerpts: z.array(z.string()).optional(),
    content: z.string().nullish(),
    publishDate: z.string().optional(),
  });
  const search = z.object({ results: z.array(z.unknown()) }).safeParse(output);
  const candidates = search.success ? search.data.results : [output];
  return candidates.flatMap((candidate) => {
    const result = resultSchema.safeParse(candidate);
    if (!result.success) return [];
    return createResearchSource({
      id: result.data.sourceId,
      url: result.data.url,
      title: result.data.title,
      preview: result.data.excerpts?.find((excerpt) => excerpt.trim()),
      publishDate: result.data.publishDate,
      excerpts: result.data.excerpts?.some((excerpt) => excerpt.trim())
        ? result.data.excerpts
        : result.data.content
          ? [result.data.content]
          : [],
    });
  });
}

function createResearchSource({
  id,
  url,
  title,
  excerpts,
  preview,
  publishDate,
}: Omit<ResearchSource, "url" | "title"> &
  Partial<Pick<ResearchSource, "url" | "title">>): ResearchSource[] {
  const result = researchSourceSchema.safeParse({
    id,
    url,
    title: title || url,
    preview,
    publishDate,
    excerpts: excerpts.filter((excerpt) => excerpt.trim().length > 0),
  });
  return result.success ? [result.data] : [];
}
